import { type RouteRecordRaw, type RouteMeta, type Router } from 'vue-router';
import { usePostHog } from '@core/analytics';
import { usePlatform } from '@core/platform';
import { useQueryClient } from '@tanstack/vue-query';
import { getCurrentUserUsersMeGetQueryKey } from '@gen/hooks';
import type { UserSchema } from '@gen/models';
// Marketing routes are statically imported because vite-ssg pre-renders them.
// All other (app-only) route components are lazy-loaded below via () => import(...)
// to keep them out of the marketing bundle and code-split the ~1.15MB main bundle.
import Marketing from '@app/presentation/screens/Marketing.vue';
import Rasklady from '@app/presentation/screens/Rasklady.vue';
import Ceny from '@app/presentation/screens/Ceny.vue';
import Faq from '@app/presentation/screens/Faq.vue';
import SpreadDetail from '@app/presentation/screens/SpreadDetail.vue';
import Karty from '@app/presentation/screens/Karty.vue';
import CardDetail from '@app/presentation/screens/CardDetail.vue';
import Metodika from '@app/presentation/screens/Metodika.vue';
import KartyMasti from '@app/presentation/screens/KartyMasti.vue';
import SuitHub from '@app/presentation/screens/SuitHub.vue';
import DaIliNet from '@app/presentation/screens/DaIliNet.vue';
import Sochetaniya from '@app/presentation/screens/Sochetaniya.vue';
import GadanieOnlayn from '@app/presentation/screens/GadanieOnlayn.vue';
import KakZadavatVoprosy from '@app/presentation/screens/KakZadavatVoprosy.vue';
import { SPREAD_SLUGS } from '@app/data/spreads';

declare module 'vue-router' {
  interface RouteMeta {
    /** Top-level shell App.vue mounts: the public marketing site vs the app frame. */
    shell: 'site' | 'app';
    /** Chrome inside the shell. 'tabs' = bottom-nav app page; 'bare' = no nav
     *  (immersive / full-screen flows, phone-frame on desktop web); 'desktop' =
     *  full-width, nav visible, opt out of the desktop phone-frame (web-demo, 404);
     *  'plain' = full-width, nav hidden, no phone-frame (admin, color-demo).
     *  Inert for shell==='site'. */
    chrome: 'tabs' | 'bare' | 'desktop' | 'plain';
    /** Marketing/SEO page: Telegram users are redirected to the app. */
    marketing?: true;
    /** Exempt from the onboarding gate (auth/payment/marketing/error flows). */
    bypassOnboarding?: true;
    /** Semantic "up" target for the back/chevron when there is no history to
     *  pop (e.g. deep-link / fresh load). Set on immersive (chrome:'bare')
     *  routes that conceptually belong under a tab. Login/onboarding/payment
     *  leave this unset (they are terminal entry points, no up target). */
    parent?: string;
    /** Optional i18n key for the FlowHeader center title. Omit when the screen
     *  has no obvious static title (the header renders fine without one). */
    title?: string;
    /** Page width inside AppShell (default: the page max width). 'full' for wide
     *  admin tables. Inert for shell==='site'. */
    width?: 'wide' | 'narrow' | 'full';
  }
}

export const HOME_ROUTE = '/spread';

// vue-router types `meta` as OPTIONAL on RouteRecordRaw, so the strict RouteMeta
// augmentation above only type-checks meta WHEN it is present. AppRoute tightens
// that: every route MUST carry `meta` (with the required shell+chrome), so a
// no-meta route is now a COMPILE error (caught by typecheck + pre-commit), not
// just by the dev assertion below. The two layers are complementary — the type
// guarantees meta presence + field shape; the dev assertion checks the
// cross-field invariants (site⇒marketing, marketing⇒bypassOnboarding).
type AppRoute = RouteRecordRaw & { meta: RouteMeta };
export const routes: AppRoute[] = [
  {
    path: '/',
    name: 'marketing',
    component: Marketing,
    meta: { shell: 'site', chrome: 'bare', marketing: true, bypassOnboarding: true },
  },
  {
    path: '/rasklady',
    name: 'rasklady',
    component: Rasklady,
    meta: { shell: 'site', chrome: 'bare', marketing: true, bypassOnboarding: true },
  },
  {
    path: '/rasklady/:slug',
    name: 'spread-detail',
    component: SpreadDetail,
    meta: { shell: 'site', chrome: 'bare', marketing: true, bypassOnboarding: true },
  },
  {
    path: '/karty',
    name: 'karty',
    component: Karty,
    meta: { shell: 'site', chrome: 'bare', marketing: true, bypassOnboarding: true },
  },
  // Static /karty sub-hubs MUST precede the dynamic /karty/:slug, or :slug swallows them.
  {
    path: '/karty/metodika',
    name: 'karty-metodika',
    component: Metodika,
    meta: { shell: 'site', chrome: 'bare', marketing: true, bypassOnboarding: true },
  },
  {
    path: '/karty/masti',
    name: 'karty-masti',
    component: KartyMasti,
    meta: { shell: 'site', chrome: 'bare', marketing: true, bypassOnboarding: true },
  },
  {
    path: '/karty/masti/:suit',
    name: 'karty-suit',
    component: SuitHub,
    meta: { shell: 'site', chrome: 'bare', marketing: true, bypassOnboarding: true },
  },
  {
    path: '/karty/da-ili-net',
    name: 'karty-da-ili-net',
    component: DaIliNet,
    meta: { shell: 'site', chrome: 'bare', marketing: true, bypassOnboarding: true },
  },
  // Static /karty sub-hub — MUST precede /karty/:slug (below), or :slug swallows it.
  {
    path: '/karty/sochetaniya',
    name: 'karty-sochetaniya',
    component: Sochetaniya,
    meta: { shell: 'site', chrome: 'bare', marketing: true, bypassOnboarding: true },
  },
  {
    path: '/karty/:slug',
    name: 'card-detail',
    component: CardDetail,
    meta: { shell: 'site', chrome: 'bare', marketing: true, bypassOnboarding: true },
  },
  {
    path: '/ceny',
    name: 'ceny',
    component: Ceny,
    meta: { shell: 'site', chrome: 'bare', marketing: true, bypassOnboarding: true },
  },
  {
    path: '/faq',
    name: 'faq',
    component: Faq,
    meta: { shell: 'site', chrome: 'bare', marketing: true, bypassOnboarding: true },
  },
  {
    // SEO landing for the `гадание онлайн / погадать сейчас` intent — a distinct
    // facet from the home page's `таро онлайн бесплатно` head (no cannibalization).
    path: '/gadanie-onlayn',
    name: 'gadanie-onlayn',
    component: GadanieOnlayn,
    meta: { shell: 'site', chrome: 'bare', marketing: true, bypassOnboarding: true },
  },
  {
    // Informational guide for the `как задавать вопросы таро` cluster. Top-level
    // (NOT under /rasklady) so it doesn't cannibalize the transactional spread
    // /rasklady/zadaj-vopros — same split-namespace rationale as /gadanie-onlayn.
    path: '/kak-zadavat-voprosy-taro',
    name: 'kak-zadavat-voprosy-taro',
    component: KakZadavatVoprosy,
    meta: { shell: 'site', chrome: 'bare', marketing: true, bypassOnboarding: true },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@app/presentation/screens/Login.vue'),
    meta: { shell: 'app', chrome: 'bare', bypassOnboarding: true },
  },
  {
    path: '/web-demo',
    name: 'web-demo',
    component: () => import('@app/presentation/screens/WebDemo.vue'),
    meta: { shell: 'app', chrome: 'desktop', bypassOnboarding: true },
  },
  {
    path: '/onboarding',
    name: 'onboarding',
    component: () => import('@app/presentation/screens/user/Onboarding.vue'),
    meta: { shell: 'app', chrome: 'bare', bypassOnboarding: true },
  },
  {
    path: '/payment',
    name: 'payment',
    component: () => import('@app/presentation/screens/payments/Payment.vue'),
    meta: { shell: 'app', chrome: 'bare', bypassOnboarding: true },
  },
  {
    path: '/payment/success',
    name: 'payment-success',
    component: () => import('@app/presentation/screens/payments/PaymentSuccess.vue'),
    meta: { shell: 'app', chrome: 'bare', bypassOnboarding: true },
  },
  {
    path: '/subscription/control',
    name: 'control-subscription',
    component: () => import('@app/presentation/screens/payments/ControlSubscription.vue'),
    meta: { shell: 'app', chrome: 'bare', parent: '/profile' },
  },
  {
    path: '/subscription/cancel-reason',
    name: 'cancel-reason',
    component: () => import('@app/presentation/screens/payments/CancelReason.vue'),
    meta: { shell: 'app', chrome: 'bare', bypassOnboarding: true, parent: '/subscription/control' },
  },
  {
    path: '/subscription/cancel-feedback',
    name: 'cancel-feedback',
    component: () => import('@app/presentation/screens/payments/CancelFeedback.vue'),
    meta: { shell: 'app', chrome: 'bare', bypassOnboarding: true, parent: '/subscription/cancel-reason' },
  },
  {
    path: '/subscription/cancel-success',
    name: 'cancel-success',
    component: () => import('@app/presentation/screens/payments/CancelSuccess.vue'),
    meta: { shell: 'app', chrome: 'bare', bypassOnboarding: true },
  },
  {
    path: '/subscription/cancel-confirm',
    name: 'cancel-confirm',
    component: () => import('@app/presentation/screens/payments/CancelConfirm.vue'),
    meta: { shell: 'app', chrome: 'bare', parent: '/subscription/cancel-feedback' },
  },
  {
    path: '/subscription/canceled',
    name: 'subscription-canceled',
    component: () => import('@app/presentation/screens/payments/CancelSuccess.vue'),
    meta: { shell: 'app', chrome: 'bare' },
  },
  // {
  //   path: '/friends',
  //   name: 'friends',
  //   component: Friends,
  //   meta: {
  //     layout: 'tabs',
  //   },
  // },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@app/presentation/screens/user/Profile.vue'),
    meta: { shell: 'app', chrome: 'tabs' },
  },
  {
    path: '/history',
    name: 'history',
    component: () => import('@app/presentation/screens/tarot/History.vue'),
    meta: { shell: 'app', chrome: 'tabs' },
  },
  {
    path: '/spread',
    name: 'spread',
    component: () => import('@app/presentation/screens/tarot/Spread.vue'),
    meta: { shell: 'app', chrome: 'tabs' },
  },
  {
    path: '/chat',
    name: 'chat',
    component: () => import('@app/presentation/screens/chat/ChatTab.vue'),
    meta: { shell: 'app', chrome: 'tabs' },
  },
  {
    path: '/chat/conversation',
    name: 'chat-conversation',
    component: () => import('@app/presentation/screens/chat/Chat.vue'),
    meta: { shell: 'app', chrome: 'bare', parent: '/chat', title: 'chat.title' },
  },
  {
    path: '/learn',
    name: 'learn',
    component: () => import('@app/presentation/screens/trainer/Learn.vue'),
    meta: { shell: 'app', chrome: 'tabs' },
  },
  // Codex (in-app card library). Static path MUST precede the dynamic :slug.
  {
    path: '/learn/codex',
    name: 'learn-codex',
    component: () => import('@app/presentation/screens/codex/Codex.vue'),
    meta: { shell: 'app', chrome: 'bare', parent: '/learn', title: 'learn.title' },
  },
  {
    path: '/learn/codex/:slug',
    name: 'learn-codex-detail',
    component: () => import('@app/presentation/screens/codex/CodexDetail.vue'),
    meta: { shell: 'app', chrome: 'bare', parent: '/learn/codex', title: 'codex.title' },
  },
  {
    path: '/learn/spread-trainer',
    name: 'learn-spread-trainer',
    component: () => import('@app/presentation/screens/trainer').then((m) => m.SpreadTrainer),
    meta: { shell: 'app', chrome: 'bare', parent: '/learn', title: 'learn.title' },
  },
  {
    path: '/learn/recognition-trainer',
    name: 'learn-recognition-trainer',
    component: () => import('@app/presentation/screens/trainer').then((m) => m.RecognitionTrainer),
    meta: { shell: 'app', chrome: 'bare', parent: '/learn', title: 'learn.title' },
  },
  {
    path: '/learn/synthesis',
    name: 'learn-synthesis',
    component: () => import('@app/presentation/screens/trainer/SpreadSynthesis.vue'),
    meta: { shell: 'app', chrome: 'bare', parent: '/learn', title: 'learn.title' },
  },
  {
    path: '/learn/drill/myth',
    name: 'learn-drill-myth',
    component: () => import('@app/presentation/screens/trainer/CardDrill.vue'),
    meta: { shell: 'app', chrome: 'bare', parent: '/learn', title: 'learn.title' },
  },
  // Reading routes are derived from the SPREADS data module — one ReadingScreen,
  // one route per spread slug. Add a spread in data/spreads.ts; its route appears
  // here automatically.
  ...SPREAD_SLUGS.map(
    (slug): AppRoute => ({
      path: `/reading/${slug}`,
      name: slug,
      component: () => import('@app/presentation/screens/tarot/ReadingScreen.vue'),
      props: { spread: slug },
      meta: { shell: 'app', chrome: 'bare', parent: '/spread' },
    }),
  ),
  {
    path: '/reading/:id',
    name: 'reading',
    component: () => import('@app/presentation/screens/tarot/Reading.vue'),
    meta: { shell: 'app', chrome: 'bare', parent: '/history' },
  },
  {
    path: '/color-demo',
    name: 'color-demo',
    component: () => import('@app/presentation/screens/ColorDemo.vue'),
    meta: { shell: 'app', chrome: 'plain' },
  },
  // Living style guide — typography, colors, combos, component gallery,
  // foundations, brand primitives. Unlisted dev/design reference (not in nav).
  {
    path: '/styleguide',
    name: 'styleguide',
    component: () => import('@app/presentation/screens/Styleguide.vue'),
    meta: { shell: 'app', chrome: 'plain', bypassOnboarding: true },
  },
  // Admin panel — FLAT sibling routes (the router is 100% flat, no `children:`).
  // Each section route renders AdminShell, which hosts the section via
  // `<component :is>` keyed by route name (no nested <router-view>). All share
  // meta:{shell:'app', chrome:'plain'} so AppNav self-hides and the shell owns
  // all chrome. STATIC paths only here; any future dynamic `:id` sibling (e.g.
  // /admin/users/:id) MUST be declared AFTER its static siblings, or :id
  // swallows them.
  {
    // /admin has no view of its own — redirect into the default section. The
    // 5-tap avatar gate (useAdmin.ts → router.push('/admin')) lands here.
    // Redirect-only record: no component, so it's skipped by the dev guardrail;
    // meta is inert here but satisfies the AppRoute type (meta is required).
    path: '/admin',
    name: 'admin',
    redirect: '/admin/overview',
    meta: { shell: 'app', chrome: 'plain' },
  },
  {
    path: '/admin/overview',
    name: 'admin-overview',
    component: () => import('@app/presentation/screens/admin/AdminShell.vue'),
    meta: { shell: 'app', chrome: 'plain' },
  },
  // Area 2 — Readings & LLM Quality. STATIC siblings only; the detail view is a
  // `?reading=<id>` query-param drawer (no dynamic `:id` route), so there is no
  // shadowing hazard here. /readings/bad and /readings/quality are declared as
  // distinct flat siblings of /readings.
  {
    path: '/admin/readings',
    name: 'admin-readings',
    component: () => import('@app/presentation/screens/admin/AdminShell.vue'),
    meta: { shell: 'app', chrome: 'plain' },
  },
  {
    path: '/admin/readings/bad',
    name: 'admin-readings-bad',
    component: () => import('@app/presentation/screens/admin/AdminShell.vue'),
    meta: { shell: 'app', chrome: 'plain' },
  },
  {
    path: '/admin/readings/quality',
    name: 'admin-readings-quality',
    component: () => import('@app/presentation/screens/admin/AdminShell.vue'),
    meta: { shell: 'app', chrome: 'plain' },
  },
  {
    path: '/admin/audit-log',
    name: 'admin-audit-log',
    component: () => import('@app/presentation/screens/admin/AdminShell.vue'),
    meta: { shell: 'app', chrome: 'plain' },
  },
  // Area 3 — Users. STATIC /admin/users MUST precede the dynamic /admin/users/:id
  // below, or :id swallows the list route (FastAPI + vue-router both match
  // top-down). The shell hosts the section keyed by route name; the detail reads
  // the :id param from the route.
  {
    path: '/admin/users',
    name: 'admin-users',
    component: () => import('@app/presentation/screens/admin/AdminShell.vue'),
    meta: { shell: 'app', chrome: 'plain' },
  },
  {
    path: '/admin/users/:id',
    name: 'admin-user-detail',
    component: () => import('@app/presentation/screens/admin/AdminShell.vue'),
    meta: { shell: 'app', chrome: 'plain' },
  },
  // Area 4 — Revenue & Engagement analytics. A single flat sibling; the dashboard
  // owns its own in-screen sub-tab strip (no extra routes).
  {
    path: '/admin/analytics',
    name: 'admin-analytics',
    component: () => import('@app/presentation/screens/admin/AdminShell.vue'),
    meta: { shell: 'app', chrome: 'plain' },
  },
  // Area 5 — Broadcasts / marketing composer. STATIC /admin/broadcasts AND
  // /admin/broadcasts/new MUST precede the dynamic /admin/broadcasts/:id below,
  // or :id swallows "new" (FastAPI + vue-router both match top-down). Flat
  // siblings; the shell hosts the section keyed by route name.
  {
    path: '/admin/broadcasts',
    name: 'admin-broadcasts',
    component: () => import('@app/presentation/screens/admin/AdminShell.vue'),
    meta: { shell: 'app', chrome: 'plain' },
  },
  {
    path: '/admin/broadcasts/new',
    name: 'admin-broadcasts-new',
    component: () => import('@app/presentation/screens/admin/AdminShell.vue'),
    meta: { shell: 'app', chrome: 'plain' },
  },
  {
    path: '/admin/broadcasts/:id',
    name: 'admin-broadcasts-edit',
    component: () => import('@app/presentation/screens/admin/AdminShell.vue'),
    meta: { shell: 'app', chrome: 'plain' },
  },
  {
    path: '/admin/debug',
    name: 'admin-debug',
    component: () => import('@app/presentation/screens/admin/AdminShell.vue'),
    meta: { shell: 'app', chrome: 'plain' },
  },
  {
    path: '/admin/tasks',
    name: 'admin-tasks',
    component: () => import('@app/presentation/screens/admin/AdminShell.vue'),
    meta: { shell: 'app', chrome: 'plain' },
  },
  {
    path: '/admin/marketing',
    name: 'admin-marketing',
    component: () => import('@app/presentation/screens/admin/AdminShell.vue'),
    meta: { shell: 'app', chrome: 'plain', width: 'full' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'error',
    component: () => import('@app/presentation/screens/Error.vue'),
    meta: { shell: 'app', chrome: 'desktop', bypassOnboarding: true },
  },
];

// Dev-only guardrail (#4): every navigable route MUST declare shell + chrome.
// This is what stops the "added a route, broke shell/redirect/onboarding" bug
// class from coming back once the 3 name-lists are deleted. Throws loudly at
// module load in dev; tree-shaken out of the prod bundle (import.meta.env.DEV).
if (import.meta.env.DEV) {
  const CHROME = new Set(['tabs', 'bare', 'desktop', 'plain']);
  const SHELL = new Set(['site', 'app']);
  for (const r of routes) {
    // 'redirect'-only records carry no component and need no shell/chrome.
    // (None today, but future redirect aliases should be skipped.)
    if ((r as { redirect?: unknown }).redirect && !r.component) continue;
    const name = String(r.name ?? r.path);
    const meta = r.meta;
    if (!meta || !SHELL.has(meta.shell as string) || !CHROME.has(meta.chrome as string)) {
      throw new Error(
        `[router] route "${name}" (${r.path}) is missing a valid meta.shell/meta.chrome. ` +
          `Got shell=${meta?.shell}, chrome=${meta?.chrome}. ` +
          `Every route must declare meta: { shell, chrome }.`,
      );
    }
    // Drift-guard: the site shell and the Telegram→app redirect must stay in
    // lockstep (today MARKETING_ROUTES === the layout:'site' set, exactly).
    if (meta.shell === 'site' && meta.marketing !== true) {
      throw new Error(
        `[router] site route "${name}" must set meta.marketing:true ` +
          `(Telegram users are redirected away from the public site).`,
      );
    }
    // Marketing pages are public — they must also bypass onboarding.
    if (meta.marketing === true && meta.bypassOnboarding !== true) {
      throw new Error(
        `[router] marketing route "${name}" must also set meta.bypassOnboarding:true.`,
      );
    }
  }
}

export function setupRouterGuards(router: Router) {
  const { posthog } = usePostHog();

  // Global navigation guard that runs before any route
  router.beforeEach(async (to) => {
    try {
      const platform = usePlatform();
      const queryClient = useQueryClient();

      // Get user synchronously from query cache for router guard
      const user = queryClient.getQueryData<UserSchema>(getCurrentUserUsersMeGetQueryKey());

      // Learn is in an admin-only private beta: non-admins can't reach the sub-screens
      // (the /learn landing stays reachable and renders a "coming soon" placeholder).
      const isLearnAdmin =
        user?.user_type === 'REGISTERED' && (user.role === 'admin' || user.role === 'owner');
      if (to.path.startsWith('/learn/') && !isLearnAdmin) {
        return '/learn';
      }

      // Redirect Telegram users away from public marketing pages into the app.
      if (platform.isTelegram && to.meta.marketing) {
        return HOME_ROUTE;
      }

      // Onboarding gate (Telegram-only; web users are guests and skip it).
      if (platform.isTelegram && !user?.is_onboarded && !to.meta.bypassOnboarding) {
        sessionStorage.setItem('redirectAfterOnboarding', to.fullPath);
        return '/onboarding';
      } else {
        const redirectPath = sessionStorage.getItem('redirectAfterOnboarding');
        if (redirectPath && to.path === HOME_ROUTE) {
          sessionStorage.removeItem('redirectAfterOnboarding');
          return redirectPath;
        }
      }

      return true;
    } catch (error) {
      console.error('Navigation guard error:', error);
      return true;
    }
  });

  router.afterEach(() => {
    posthog.capture('$pageview');
  });
}
